<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
    <div class="p-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact Support</h2>

      <form @submit.prevent="submitForm" class="space-y-4">
        <HoneypotFields :model-value="form" />

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputLabel for="name" value="Name" />
            <TextInput
              id="name"
              v-model="form.name"
              type="text"
              class="mt-1 block w-full"
              required
              autofocus
            />
            <InputError :message="errors.name" class="mt-2" />
          </div>

          <div>
            <InputLabel for="email" value="Email" />
            <TextInput
              id="email"
              v-model="form.email"
              type="email"
              class="mt-1 block w-full"
              required
            />
            <InputError :message="errors.email" class="mt-2" />
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <InputLabel for="phone" value="Phone Number (Optional)" />
            <PhoneInput
              id="phone"
              v-model="form.phone"
              class="block w-full border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600 rounded-md shadow-sm"
            />
            <InputError :message="errors.phone" class="mt-2" />
          </div>

          <div>
            <InputLabel for="category" value="Category" />
            <select
              id="category"
              v-model="form.category"
              class="mt-1 block w-full border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600 rounded-md shadow-sm"
              required
            >
              <option value="">Select a category...</option>
              <option value="sales">Sales</option>
              <option value="billing">Billing</option>
              <option value="technical_support">Technical Support</option>
              <option value="account_setup">Account & Setup</option>
              <option value="feature_request">Feature Request</option>
            </select>
            <InputError :message="errors.category" class="mt-2" />
          </div>
        </div>

        <div>
          <InputLabel for="subject" value="Subject" />
          <TextInput
            id="subject"
            v-model="form.subject"
            type="text"
            class="mt-1 block w-full"
            required
            placeholder="Brief summary of your inquiry"
          />
          <InputError :message="errors.subject" class="mt-2" />
        </div>

        <div>
          <InputLabel for="message" value="Message" />
          <textarea
            id="message"
            v-model="form.message"
            rows="6"
            class="mt-1 block w-full border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600 rounded-md shadow-sm"
            required
            placeholder="Please provide details about your inquiry..."
          ></textarea>
          <InputError :message="errors.message" class="mt-2" />
        </div>

        <div class="flex justify-end space-x-3 mt-6">
          <SecondaryButton @click="$emit('close')">
            Cancel
          </SecondaryButton>
          <PrimaryButton :disabled="isSubmitting" :class="{ 'opacity-50': isSubmitting }">
            {{ isSubmitting ? 'Sending...' : 'Send Message' }}
          </PrimaryButton>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { usePage } from '@inertiajs/vue3';
import InputLabel from '@/Components/InputLabel.vue';
import TextInput from '@/Components/TextInput.vue';
import InputError from '@/Components/InputError.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import PhoneInput from '@/Components/PhoneInput.vue';
import HoneypotFields from '@/Components/HoneypotFields.vue';
import axios from 'axios';

const emit = defineEmits(['submitted', 'close']);
const page = usePage();

const isSubmitting = ref(false);
const errors = ref({});
const honeypot = page.props.honeypot;

const form = reactive({
  name: '',
  email: '',
  phone: '',
  category: '',
  subject: '',
  message: '',
  [honeypot.nameFieldName]: '',
  [honeypot.validFromFieldName]: honeypot.encryptedValidFrom,
});

// Auto-fill name and email for logged-in users
onMounted(() => {
  if (page.props.auth?.user) {
    form.name = page.props.auth.user.name || '';
    form.email = page.props.auth.user.email || '';

    // Try to get phone from current team if available
    if (page.props.auth.user.current_team?.settings?.phone_number) {
      form.phone = page.props.auth.user.current_team.settings.phone_number;
    }
  }
});

const submitForm = async () => {
  isSubmitting.value = true;
  errors.value = {};

  try {
    const response = await axios.post(route('support.store'), form);
    emit('submitted', response.data);
    emit('close');
  } catch (error) {
    if (error.response && error.response.data && error.response.data.errors) {
      errors.value = error.response.data.errors;
    } else {
      console.error('Error submitting support form:', error);
      errors.value = { general: 'An error occurred. Please try again.' };
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>
